import { Command, SearchInput } from '../input';
import { Dialog, DialogWindow, useDialogManager } from '../dialog';
import { Link } from '../buttons';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { ErrorMessage } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { CustomErrorBoundary } from '../customErrorBoundary';
import { DialogResults } from './DialogResults';
import { QuickSearchDialogResults } from './quickSearchDialogResults';

const CustomSearchInput = styled(SearchInput)`
    width: 100%;
    border: none;
    border-bottom: 1px #ddd solid;
    border-radius: 0;
    padding: 20px 30px;
    height: auto;
    flex: 0;
    outline: none;

    &:focus {
        outline: none;
        border: none;
        border-bottom: 1px #ddd solid;
    }
`;

// Create a empty state for the dialog
const GrayText = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #666;

    ${Link} {
        margin-top: 10px;
        font-size: 12px;
    }
`;

const KnowlageBaseLink = styled(Link)`
    font-size: 12px;
    display: block;
    padding: 10px 20px;
    margin: auto;
`;

const EscCommand = styled(Command)`
    cursor: pointer;
`;

const CustomWindow = styled(DialogWindow)`
    width: 600px;
    height: 100%;
    padding: 0;
    overflow: hidden;
`;

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
`;

export default function QuickSearchDialog() {
    const { closeDialog, currentDialogId } = useDialogManager();
    const [filter, setFilter] = useState('');
    const filterRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        filterRef.current?.focus();
    }, []);

    // Close with ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeDialog(currentDialogId);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('keydown', handleEsc);
        };
    }, []);

    return (
        <Dialog
            windowDecorator={CustomWindow}
            showCloseButton={false}
            closeOnOverlayClick
            footerContent={
                <KnowlageBaseLink href={'https://zakeke.zendesk.com/hc/'} target='_blank' data-track='quick search knowlage base'>
                    {T._('Zakeke knowlage base', 'Admin')}
                </KnowlageBaseLink>
            }
        >
            <DialogContent>
                <CustomSearchInput
                    data-track='quick search input focus'
                    ref={filterRef}
                    type='text'
                    placeholder={T._('Quick search...', 'Admin')}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    rightElement={
                        <EscCommand
                            data-track='quick search esc command'
                            onClick={() => {
                                closeDialog(currentDialogId);
                            }}
                        >
                            ESC
                        </EscCommand>
                    }
                />

                {filter != '' && (
                    <Suspense>
                        <DialogResults>
                            <CustomErrorBoundary render={() => <ErrorMessage>{T._('Error fetching search results.', 'Admin')}</ErrorMessage>}>
                                <QuickSearchDialogResults filter={filter} />
                            </CustomErrorBoundary>
                        </DialogResults>
                    </Suspense>
                )}

                {filter === '' && (
                    <GrayText>
                        <span>{T._('Start typing to search', 'Admin')}</span>
                        <Link
                            data-track='quick search learn more'
                            href={'https://zakeke.zendesk.com/hc/en-us/articles/12634522039836-How-to-search-Tools-Setting-and-more'}
                            target='_blank'
                        >
                            {T._('Learn more about search', 'Admin')}
                        </Link>
                    </GrayText>
                )}
            </DialogContent>
        </Dialog>
    );
}

export type DialogResultItem = {
    name: string;
    url: string;
    icon: string;
};
